// ProgressMeter — a single ratio (0–100%). Two shapes:
//   variant="bar"   — a linear meter (default)
//   variant="gauge" — a 180° semicircle gauge
// The track is a lighter step of the fill ramp so state reads across the
// whole bar. `tone` sets severity color; "auto" derives it from thresholds
// (≥80 good, ≥50 warn, else danger). Accessible via role=progressbar.
//
//   <ProgressMeter value={92} label="Compliance rate" tone="auto" />
//   <ProgressMeter value={64} max={100} variant="gauge" label="Capacity" />

export type MeterTone = 'brand' | 'good' | 'warn' | 'danger' | 'auto';

export interface ProgressMeterProps {
  value: number;
  max?: number;
  label?: string;
  /** Show the numeric value/percent. Default true. */
  showValue?: boolean;
  /** Format as percent (value already 0–100) vs value/max. Default percent when max===100. */
  suffix?: string;
  tone?: MeterTone;
  variant?: 'bar' | 'gauge';
  size?: number; // gauge diameter
  ariaLabel?: string;
  className?: string;
}

function resolveTone(pct: number, tone: MeterTone): string {
  if (tone === 'good') return 'var(--good)';
  if (tone === 'warn') return 'var(--warn)';
  if (tone === 'danger') return 'var(--danger)';
  if (tone === 'brand') return 'var(--pr)';
  // auto
  return pct >= 80 ? 'var(--good)' : pct >= 50 ? 'var(--warn)' : 'var(--danger)';
}

export default function ProgressMeter({
  value,
  max = 100,
  label,
  showValue = true,
  suffix,
  tone = 'brand',
  variant = 'bar',
  size = 150,
  ariaLabel,
  className,
}: ProgressMeterProps) {
  const pct = Math.max(0, Math.min(100, (value / (max || 1)) * 100));
  const color = resolveTone(pct, tone);
  const valueText = suffix != null ? `${Math.round(value)}${suffix}` : max === 100 ? `${Math.round(value)}%` : `${value} / ${max}`;

  if (variant === 'gauge') {
    const r = (size - 18) / 2;
    const cx = size / 2;
    const cy = size / 2;
    const circ = Math.PI * r; // half circumference
    const dash = (pct / 100) * circ;
    return (
      <div className={['ui-chart', className].filter(Boolean).join(' ')} style={{ alignItems: 'center', gap: 'var(--sp-1)' }}>
        <svg width={size} height={size / 2 + 14} viewBox={`0 0 ${size} ${size / 2 + 14}`} role="progressbar" aria-valuenow={Math.round(value)} aria-valuemin={0} aria-valuemax={max} aria-label={ariaLabel ?? label}>
          <path d={`M${cx - r} ${cy} A${r} ${r} 0 0 1 ${cx + r} ${cy}`} fill="none" stroke="var(--chart-track)" strokeWidth={12} strokeLinecap="round" />
          <path d={`M${cx - r} ${cy} A${r} ${r} 0 0 1 ${cx + r} ${cy}`} fill="none" stroke={color} strokeWidth={12} strokeLinecap="round" strokeDasharray={`${dash} ${circ}`} />
          <text x={cx} y={cy - 4} textAnchor="middle" className="ui-chart-value" fontSize={22} fontWeight={700} fill="var(--text-strong)">{valueText}</text>
          {label && <text x={cx} y={cy + 12} textAnchor="middle" fontSize={11} fill="var(--text-muted)">{label}</text>}
        </svg>
      </div>
    );
  }

  return (
    <div className={className}>
      {(label || showValue) && (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 'var(--sp-2)' }}>
          {label && <span style={{ fontSize: 'var(--fs-sm)', fontWeight: 600, color: 'var(--text)' }}>{label}</span>}
          {showValue && <span className="ui-chart-value" style={{ fontSize: 'var(--fs-sm)', fontWeight: 700, color: 'var(--text-strong)' }}>{valueText}</span>}
        </div>
      )}
      <div
        role="progressbar"
        aria-valuenow={Math.round(value)}
        aria-valuemin={0}
        aria-valuemax={max}
        aria-label={ariaLabel ?? label}
        style={{ height: 8, borderRadius: 'var(--r-full)', background: 'var(--chart-track)', overflow: 'hidden' }}
      >
        <div style={{ width: `${pct}%`, height: '100%', borderRadius: 'var(--r-full)', background: color, transition: 'width var(--dur-slow) var(--ease-out)' }} />
      </div>
    </div>
  );
}
